import { Alert, IconButton, Snackbar } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';

export interface ToastProps {
    message: string;
    severity?: 'success' | 'info' | 'warning' | 'error';
    duration?: number;
}

export const useToast = () => {
    const [open, setOpen] = useState(false);
    const [innerMessage, setMessage] = useState('');
    const [innerSeverity, setSeverity] = useState<'success' | 'info' | 'warning' | 'error'>(
        'success'
    );
    const currentTimerRef = useRef<NodeJS.Timeout | null>(null);

    const showToast = useCallback(
        ({ message, severity = 'success', duration = 10000 }: ToastProps) => {
            // Clear previous timer to avoid interferences
            if (currentTimerRef.current) clearTimeout(currentTimerRef.current);

            setMessage(message);
            setSeverity(severity);
            setOpen(true);

            currentTimerRef.current = setTimeout(() => setOpen(false), duration);
        },
        []
    );

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const Toast = useMemo(() => {
        return () => (
            <Snackbar open={open} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={innerSeverity} style={{ display: 'flex', alignItems: 'center' }}>
                    {innerMessage}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        onClick={handleClose}
                        sx={{ marginLeft: '5px' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Alert>
            </Snackbar>
        );
    }, [open, innerMessage, innerSeverity, handleClose]);

    return [Toast, showToast] as const;
};
